/**
 * Maps a workflow's open issues (see `useWorkflowOpenIssues`) onto the nodes
 * they're filed against, for the flow-designer canvas's per-node issue pill.
 *
 * An issue belongs to a node when:
 *  - `subjectKind === 'node'` and `subjectNodeId` matches the node, or
 *  - `subjectKind === 'assertion'` and the assertion key matches either the
 *    node itself (a standalone `assertion` node, keyed by its own id) or one
 *    of the node's inline assertions (an `action` node's `inlineAssertions`).
 *
 * @module ui/pages/FlowDesigner/hooks/issuesForNode
 */
import type { OpenWorkflowIssueSummary } from '../../flow-list/types';
import { issueHref } from '../../../../utils/issueLinks';

export type NodeOpenIssueSummary = Pick<
  OpenWorkflowIssueSummary,
  'id' | 'shortcode' | 'title' | 'severity'
> & {
  /** `packages/ui` must not hardcode the app route — built here instead. */
  href: string;
};

/** The subset of a node's shape this needs — kept narrow so tests don't need a full WorkflowNode. */
type IssueMatchNode = {
  id: string;
  type?: string;
  data?: {
    inlineAssertions?: Array<{ id?: string }>;
  };
};

export function issuesForNode(
  node: IssueMatchNode,
  openIssues: OpenWorkflowIssueSummary[]
): NodeOpenIssueSummary[] {
  if (openIssues.length === 0) return [];

  return openIssues
    .filter((issue) => {
      if (issue.subjectKind === 'node') {
        return issue.subjectNodeId === node.id;
      }
      if (issue.subjectKind === 'assertion') {
        if (node.type === 'assertion' && node.id === issue.subjectAssertionKey) return true;
        const inlineAssertions = node.data?.inlineAssertions;
        return (
          Array.isArray(inlineAssertions) &&
          inlineAssertions.some((a) => a?.id === issue.subjectAssertionKey)
        );
      }
      return false;
    })
    .map((issue) => ({
      id: issue.id,
      shortcode: issue.shortcode,
      title: issue.title,
      severity: issue.severity,
      href: issueHref(issue.id),
    }));
}
